import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { crmApi } from "../lib/crm-api";
import { queryKeys } from "../lib/queryKeys";
import { SALES_TEAM_SLUGS } from "../lib/sales-teams";
import { useTheme, type Theme } from "../lib/theme";
import { absoluteTime, formatInZone } from "../lib/time";
import {
  DEFAULT_LANDING_VIEW,
  LANDING_PRESETS,
  savedViewIdFrom,
  userIdFromView,
  type ViewId,
} from "../lib/views";
import { Avatar } from "../components/ui/Avatar";
import { cn } from "../components/ui/cn";
import { Panel } from "../components/ui/Panel";
import { Toggle } from "../components/ui/Toggle";
import { NotificationSettings } from "../components/settings/NotificationSettings";
import { slugify } from "./settings/shared";
import { EmojiPicker } from "../components/editor/EmojiPicker";
import type {
  ApiKeyScope,
  CreatedApiKey,
  GoogleCapability,
  User,
} from "../types";

/**
 * An agent's own settings.
 *
 * Split from the admin page because they answer different questions: this one
 * is "how do I want the app to look and how should I appear", which everyone
 * needs, and the other is "how does the desk work", which is a manager's job.
 * Mixing them means every agent scrolls past pipelines and SLA targets they
 * cannot change to reach their own theme.
 */

/**
 * Avatar tints, as pale backgrounds with a darker ink of the same hue.
 *
 * Pastel rather than saturated: these appear on every row of a dense list, and
 * a column of bright circles pulls the eye away from the SLA colours, which are
 * the only thing on that screen that should be shouting.
 */
const COLORS: { bg: string; fg: string; label: string }[] = [
  { bg: "#fce7f2", fg: "#b91562", label: "Pink" },
  { bg: "#fee2e2", fg: "#b91c1c", label: "Red" },
  { bg: "#ffedd5", fg: "#c2410c", label: "Orange" },
  { bg: "#fef3c7", fg: "#b45309", label: "Amber" },
  { bg: "#dcfce7", fg: "#15803d", label: "Green" },
  { bg: "#ccfbf1", fg: "#0f766e", label: "Teal" },
  { bg: "#dbeafe", fg: "#1d4ed8", label: "Blue" },
  { bg: "#e0e7ff", fg: "#4338ca", label: "Indigo" },
  { bg: "#f3e8ff", fg: "#7e22ce", label: "Violet" },
  { bg: "#e2e8f0", fg: "#475569", label: "Slate" },
];

export function PersonalSettings({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const [theme, setTheme] = useTheme();
  const [error, setError] = useState<string | null>(null);

  const me = useQuery({ queryKey: queryKeys.mySettings, queryFn: api.mySettings });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.mySettings });
    // The avatar appears on every list row and in the sidebar.
    void queryClient.invalidateQueries({ queryKey: queryKeys.users });
    // The sidebar draws its circle from the session user, not the list.
    void queryClient.invalidateQueries({ queryKey: queryKeys.me });
  };

  const setAvatar = useMutation({
    mutationFn: (patch: { color: string | null }) => api.setMyAvatar(patch),
    onSuccess: invalidate,
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not save"),
  });

  const setEmoji = useMutation({
    mutationFn: (emoji: string | null) => api.setMyEmoji(emoji),
    onSuccess: invalidate,
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not save"),
  });

  const setBoard = useMutation({
    mutationFn: (board: string | null) => api.setMyBoard(board),
    onSuccess: () => {
      invalidate();
      /* The composer says which board a task is going to before it is raised,
         and it reads that from the server rather than from this page. */
      void queryClient.invalidateQueries({ queryKey: ["crm", "lane-boards"] });
    },
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not save"),
  });

  const setDefaultView = useMutation({
    mutationFn: (view: string | null) => api.setMyDefaultView(view),
    onSuccess: invalidate,
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not save"),
  });

  const setScope = useMutation({
    mutationFn: (scope: "all" | "mine") => api.setMyCompaniesScope(scope),
    onSuccess: invalidate,
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not save"),
  });

  const settings = me.data;

  return (
    <>
      <Panel
        title="Appearance"
      >
        <div className="inline-flex rounded-md border border-line p-0.5">
          {(
            [
              { value: "light", label: "Light" },
              { value: "dark", label: "Dark" },
              { value: "system", label: "Match system" },
            ] as { value: Theme; label: string }[]
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setTheme(option.value)}
              aria-pressed={theme === option.value}
              className={cn(
                "rounded px-3 py-1 text-xs transition-colors",
                theme === option.value
                  ? "bg-brand-600 text-white"
                  : "text-muted hover:text-brand-600 dark:hover:text-brand-400",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Panel>

      <Panel
        title="Your avatar"
      >
        {/* `items-start`: the preview is a fixed circle and the column beside
            it grew a second row, so centring left the circle floating in the
            middle of it rather than beside the swatches it previews. */}
        <div className="flex items-start gap-4">
          <Avatar
            size="md"
            className="size-14 text-base"
            agent={{
              id: user.id,
              name: user.name,
              color: settings?.avatarColor ?? null,
            }}
          />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1">
              {COLORS.map((option) => (
                <button
                  key={option.bg}
                  type="button"
                  onClick={() => setAvatar.mutate({ color: option.bg })}
                  aria-label={option.label}
                  title={option.label}
                  aria-pressed={settings?.avatarColor === option.bg}
                  style={{ backgroundColor: option.bg, color: option.fg }}
                  className={cn(
                    "flex size-7 items-center justify-center rounded-full text-[10px] font-semibold",
                    "ring-offset-2 ring-offset-surface",
                    settings?.avatarColor === option.bg && "ring-2 ring-content",
                  )}
                >
                  {initials(user.name)}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setAvatar.mutate({ color: null })}
                className="rounded px-2 text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
              >
                Automatic
              </button>
            </div>

            {/*
              The glyph, under the colours it sits beside in the sidebar.
              
              Here rather than in its own panel because it answers the same
              question the tints do -- how colleagues pick you out of a list --
              and a second panel would suggest it was a second decision.

              Everyone sees it, which is the point and is worth saying on
              screen: a person choosing a private label would choose
              differently from one choosing how thirty colleagues will find
              their name.
            */}
            <div className="mt-3 flex items-center gap-2 border-t border-line pt-3">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-line text-base leading-none">
                {settings?.emoji ?? (
                  <span className="text-[10px] text-faint">—</span>
                )}
              </span>

              <EmojiPicker onPick={(emoji) => setEmoji.mutate(emoji)} />

              {settings?.emoji ? (
                <button
                  type="button"
                  onClick={() => setEmoji.mutate(null)}
                  className="rounded px-2 text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
                >
                  Remove
                </button>
              ) : null}

              <p className="min-w-0 text-xs text-muted">
                Shown before your name in everyone&rsquo;s sidebar.
              </p>
            </div>

            {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
          </div>
        </div>
      </Panel>

      <DefaultViewPanel
        value={settings?.helpdeskDefaultView ?? null}
        onChange={(view) => setDefaultView.mutate(view)}
        saving={setDefaultView.isPending}
      />

      <GoogleConnectionPanel />
      <LaneConnectionPanel />
      <TaskBoardPanel
        value={settings?.crmBoard ?? null}
        onChange={(board) => setBoard.mutate(board)}
        saving={setBoard.isPending}
      />
      <CompaniesScopePanel
        value={settings?.crmCompaniesScope ?? "all"}
        onChange={(scope) => setScope.mutate(scope)}
        saving={setScope.isPending}
      />

      <ApiKeysPanel />

      <NotificationSettings />
    </>
  );
}


/**
 * The agent's own Google account.
 *
 * Deliberately here and not in admin Settings, beside the channels. Those
 * authorise a shared *inbox* so the helpdesk can read and send as support@;
 * this authorises a *person*, so a meeting lands in their calendar and the
 * invitation arrives from them. They are different grants over the same
 * provider and are revoked independently, which is exactly why they live in
 * different places.
 *
 * The two halves are asked for separately because they are very different
 * asks. Writing to your calendar is small; letting the CRM read your channel is
 * not, and bundling them would mean anybody who wanted their meetings in their
 * diary had to hand over their inbox to get it.
 */
function GoogleConnectionPanel() {
  const queryClient = useQueryClient();
  /* Calendar and sending, to start with: the two a field rep needs for a
     meeting to go in their diary and its follow-up to go from them. Reading
     their mail is the bigger ask and stays something they choose. */
  const [wanted, setWanted] = useState<GoogleCapability[]>(["calendar", "send"]);

  const connection = useQuery({
    queryKey: queryKeys.googleConnection,
    queryFn: api.googleConnection,
  });

  const disconnect = useMutation({
    mutationFn: () => api.disconnectGoogle(),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: queryKeys.googleConnection,
      }),
  });

  // Set by the OAuth callback on its way back to this page.
  const outcome = new URLSearchParams(window.location.search).get("google");

  if (connection.data && !connection.data.available) {
    return (
      <Panel title="Google">
        <p className="text-xs text-muted">
          Connecting a Google account is not configured on this server.
        </p>
      </Panel>
    );
  }

  const has = (capability: GoogleCapability): boolean =>
    connection.data?.capabilities.includes(capability) ?? false;

  const toggle = (capability: GoogleCapability): void =>
    setWanted((was) =>
      was.includes(capability)
        ? was.filter((entry) => entry !== capability)
        : [...was, capability],
    );

  return (
    <Panel title="Google">
      {connection.data?.connected ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-content">
            Connected
            {connection.data.googleEmail
              ? ` as ${connection.data.googleEmail}`
              : ""}
          </span>
          <span className="text-xs text-muted">
            {connection.data.capabilities.length === 0
              ? "no access granted"
              : connection.data.capabilities
                  .map((capability) => CAPABILITY_LABELS[capability])
                  .join(", ")}
          </span>
          <button
            type="button"
            onClick={() => disconnect.mutate()}
            disabled={disconnect.isPending}
            className="rounded-md border border-line px-2 py-1 text-xs text-content hover:bg-surface-2 disabled:opacity-50"
          >
            {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
          </button>
        </div>
      ) : null}

      <div className={connection.data?.connected ? "mt-3" : undefined}>
        <div className="mb-2 flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => toggle("calendar")}
            aria-pressed={wanted.includes("calendar")}
            className={chipClass(wanted.includes("calendar"))}
          >
            Calendar
          </button>
          <button
            type="button"
            onClick={() => toggle("gmail")}
            aria-pressed={wanted.includes("gmail")}
            className={chipClass(wanted.includes("gmail"))}
          >
            Gmail
          </button>
          <button
            type="button"
            onClick={() => toggle("send")}
            aria-pressed={wanted.includes("send")}
            className={chipClass(wanted.includes("send"))}
          >
            Send email
          </button>
        </div>

        <p className="mb-2 text-xs text-muted">
          Calendar puts your meetings in your own diary and sends the
          invitations from you, with a Meet link for online ones. Gmail lets
          your own correspondence be filed against a company in the CRM. Send
          email lets a meeting's follow-up go from your own address — it can
          send, and cannot read anything.
        </p>

        {/*
          A link, not a fetch: the authorisation server has to see a top-level
          navigation, and an XHR redirect to another origin would be blocked.
        */}
        <a
          href={`/api/google/oauth/start?scopes=${wanted.join(",")}`}
          className={cn(
            "inline-flex rounded-md px-3 py-1.5 text-xs font-medium text-white",
            wanted.length > 0
              ? "bg-brand-600 hover:bg-brand-700"
              : "pointer-events-none bg-brand-600/40",
          )}
        >
          {connection.data?.connected
            ? "Change what Google can do"
            : "Connect Google"}
        </a>

        {/* Said plainly, because it is the one surprising thing about OAuth:
            Google never widens an existing grant, so adding the second half
            means going through consent again rather than topping it up. */}
        {connection.data?.connected &&
        wanted.some((capability) => !has(capability)) ? (
          <p className="mt-1.5 text-xs text-muted">
            Google cannot widen a grant you have already given, so this asks for
            the whole set again.
          </p>
        ) : null}
      </div>

      {outcome === "denied" ? (
        <p className="mt-2 text-xs text-amber-600">
          Google sign-in was cancelled.
        </p>
      ) : null}
      {outcome === "failed" ? (
        <p className="mt-2 text-xs text-red-600">
          Could not complete Google sign-in. Try again.
        </p>
      ) : null}
    </Panel>
  );
}

const CAPABILITY_LABELS: Record<GoogleCapability, string> = {
  calendar: "Calendar",
  gmail: "Gmail",
  send: "Send email",
};

function chipClass(on: boolean): string {
  return cn(
    "h-7 rounded-md border px-2 text-xs transition-colors",
    on
      ? "border-brand-600 bg-brand-600 font-medium text-white"
      : "border-line bg-surface text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
  );
}

/** What the CRM's company list opens on. */
function CompaniesScopePanel({
  value,
  onChange,
  saving,
}: {
  value: "all" | "mine";
  onChange: (scope: "all" | "mine") => void;
  saving: boolean;
}) {
  return (
    <Panel title="CRM companies">
      <span className="block text-xs text-muted">The company list opens on</span>
      <div className="mt-1 inline-flex rounded-md border border-line p-0.5">
        {(
          [
            { value: "all", label: "All companies" },
            { value: "mine", label: "My companies" },
          ] as const
        ).map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={saving}
            onClick={() => onChange(option.value)}
            aria-pressed={value === option.value}
            className={cn(
              "rounded px-3 py-1 text-xs",
              value === option.value
                ? "bg-brand-600 font-medium text-white"
                : "text-muted hover:text-content",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
      <span className="mt-1 block text-[11px] text-faint">
        {value === "mine"
          ? "The accounts you manage. The list's All switch still shows everybody's."
          : "Everybody's accounts. The list's Mine switch narrows it to yours."}
      </span>
    </Panel>
  );
}

/**
 * Which Helpdesk view opening the Helpdesk lands on.
 *
 * All open tickets unless somebody chooses otherwise, which is what the desk
 * has always opened on and is the only view never empty for a new agent. An
 * agent who works their own queue all day can land on it instead of stepping
 * through everybody's open work to reach it every morning.
 *
 * The options are the presets, the saved views this agent can see, and the
 * colleagues' queues they have starred -- the sidebar's own vocabulary, so
 * the choice is always something they could also have clicked. Every agent's
 * queue would be thirty rows nobody wants as a landing page; the few that are
 * are the ones somebody has already starred.
 */
function DefaultViewPanel({
  value,
  onChange,
  saving,
}: {
  value: string | null;
  onChange: (view: string | null) => void;
  saving: boolean;
}) {
  const saved = useQuery({ queryKey: queryKeys.savedViews, queryFn: api.savedViews });
  const favourites = useQuery({
    queryKey: queryKeys.favourites,
    queryFn: api.favourites,
  });
  const users = useQuery({ queryKey: queryKeys.users, queryFn: api.users });

  const queueKeys = new Set(
    (favourites.data?.keys ?? []).filter((key) => /^user-\d+$/.test(key)),
  );
  /* A queue chosen and then unstarred stays listed, so the select never shows
     a blank for a choice that is still in force. */
  if (value && /^user-\d+$/.test(value)) queueKeys.add(value);

  const queues = [...queueKeys].flatMap((key) => {
    const agent = (users.data ?? []).find(
      (entry) => entry.id === userIdFromView(key as ViewId),
    );
    return agent ? [{ key, label: agent.name }] : [];
  });

  /* Stored, but naming a saved view that has gone. Said, rather than shown
     as All open, because the setting has not changed -- only what the
     Helpdesk can do with it. */
  const savedId = value ? savedViewIdFrom(value as ViewId) : null;
  const missing =
    savedId !== null &&
    saved.data !== undefined &&
    !saved.data.some((entry) => entry.id === savedId);

  return (
    <Panel title="Helpdesk">
      <label className="block">
        <span className="block text-xs text-muted">The Helpdesk opens on</span>
        <select
          value={missing ? "" : (value ?? "")}
          disabled={saving}
          onChange={(event) =>
            onChange(event.target.value === "" ? null : event.target.value)
          }
          className="mt-1 h-8 rounded-md border border-line bg-app px-2 text-sm text-content outline-none focus:border-brand-400"
        >
          {/* The empty value is the default, not a stored "open": somebody
              who never chose keeps following whatever the desk opens on. */}
          <option value="">All open tickets</option>
          <optgroup label="Views">
            {LANDING_PRESETS.filter(
              (view) => view.id !== DEFAULT_LANDING_VIEW,
            ).map((view) => (
              <option key={view.id} value={view.id}>
                {view.label}
              </option>
            ))}
          </optgroup>
          {(saved.data ?? []).length > 0 ? (
            <optgroup label="Saved views">
              {(saved.data ?? []).map((view) => (
                <option key={view.id} value={`saved-${view.id}`}>
                  {view.name}
                </option>
              ))}
            </optgroup>
          ) : null}
          {queues.length > 0 ? (
            <optgroup label="Starred queues">
              {queues.map((queue) => (
                <option key={queue.key} value={queue.key}>
                  {queue.label}
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
        <span className="mt-1 block text-[11px] text-faint">
          {missing
            ? "The saved view you chose has been deleted, so the Helpdesk opens on All open tickets."
            : value === null
              ? "Everybody's open work. Choose My open tickets to land on your own queue."
              : "Where the Helpdesk tile and a fresh visit take you. Every other view is still in the sidebar."}
        </span>
      </label>
    </Panel>
  );
}

/**
 * Where this agent's CRM tasks land in Lane.
 *
 * Theirs to set, which is the whole reason it exists. The board used to follow
 * team membership and nothing else -- an admin's to change, so a rep watching
 * their tasks pile up on the wrong board could not do anything about it except
 * ask somebody. Covering for the other half of the desk is routine besides,
 * and being in the Internal Sales team does not mean the work is.
 *
 * "Follow my team" stays the default and is a real answer, not an empty one:
 * it keeps moving with team membership, which is right for the people who have
 * never thought about this and never should have to.
 *
 * The options are the boards Lane actually has, not a hardcoded pair. A desk
 * that has not split sales in two sees one, and a board renamed in Lane is
 * renamed here -- the same reason `CRM_LANE_BOARDS` matches on names.
 */
function TaskBoardPanel({
  value,
  onChange,
  saving,
}: {
  value: string | null;
  onChange: (board: string | null) => void;
  saving: boolean;
}) {
  const boards = useQuery({
    queryKey: queryKeys.crmLaneBoards(),
    queryFn: () => crmApi.laneBoards(),
    staleTime: 5 * 60_000,
  });

  /* Nothing to choose between when Lane is not connected, or when the desk
     files everything to one board. Saying so beats an empty select. */
  const options = (boards.data?.boards ?? []).flatMap((board) => {
    const slug = slugForBoard(board);
    return slug === null ? [] : [{ board, slug }];
  });

  return (
    <Panel title="CRM tasks">
      {boards.isLoading ? (
        <p className="text-xs text-faint">Loading…</p>
      ) : options.length === 0 ? (
        <p className="text-xs text-faint">
          No Lane board is set up for CRM tasks
          {(boards.data?.unmatched.length ?? 0) > 0
            ? ` — nothing in Lane matches ${boards.data?.unmatched.join(" or ")}, which a manager needs to check.`
            : ", so there is nothing to choose between yet."}
        </p>
      ) : (
        <label className="block">
          <span className="block text-xs text-muted">My tasks go to</span>
          <select
            value={value ?? ""}
            disabled={saving}
            onChange={(event) =>
              onChange(event.target.value === "" ? null : event.target.value)
            }
            className="mt-1 h-8 rounded-md border border-line bg-app px-2 text-sm text-content outline-none focus:border-brand-400"
          >
            <option value="">Follow my team</option>
            {options.map(({ board, slug }) => (
              <option key={board.id} value={slug}>
                {board.name}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-[11px] text-faint">
            {value === null
              ? "Whichever sales team you are in, or Internal Sales if you are in neither."
              : "Every task you raise from a company goes here, whatever team you are in."}
          </span>
        </label>
      )}
    </Panel>
  );
}

/**
 * The slug stored for a board, or null when it is not one of the sales desks.
 *
 * Not the board's id. Lane's ids are UUIDs and a board deleted and remade
 * keeps its name and loses its id, so a stored id would quietly stop matching
 * and put everybody back on the fallback board with nothing to show why.
 *
 * Both the board's name and its space are candidates, matching `boardFor` --
 * a desk may have put the internal/external split at either level, and a board
 * called "Chases" inside an "External Sales" space is stored as the space.
 * Null filters the board out of the picker: `CRM_LANE_BOARDS` could name a
 * board that is not a sales desk at all, and offering it would produce a value
 * the server is right to reject.
 */
function slugForBoard(board: { name: string; spaceName: string }): string | null {
  return (
    [slugify(board.name), slugify(board.spaceName)].find((slug) =>
      SALES_TEAM_SLUGS.includes(slug),
    ) ?? null
  );
}

/**
 * The agent's own Lane account.
 *
 * Personal rather than admin: the whole point of connecting is that tasks are
 * attributed to the individual, so the connection belongs to the individual
 * too. Disconnecting revokes the token with Lane rather than only forgetting it
 * here -- a credential we have stopped using is not the same as one that has
 * stopped working.
 */
function LaneConnectionPanel() {
  const queryClient = useQueryClient();

  const connection = useQuery({
    queryKey: queryKeys.laneConnection,
    queryFn: api.laneConnection,
  });

  const disconnect = useMutation({
    mutationFn: () => api.disconnectLane(),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.laneConnection }),
  });

  // Set by the OAuth callback on its way back to this page.
  const outcome = new URLSearchParams(window.location.search).get("lane");

  if (connection.data && !connection.data.available) {
    return (
      <Panel
        title="Lane"
      >
        <p className="text-xs text-muted">
          Lane sign-in is not configured on this server.
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      title="Lane"
    >
      {connection.data?.connected ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-content">
            Connected
            {connection.data.laneName ? ` as ${connection.data.laneName}` : ""}
          </span>
          <button
            type="button"
            onClick={() => disconnect.mutate()}
            disabled={disconnect.isPending}
            className="rounded-md border border-line px-2 py-1 text-xs text-content hover:bg-surface-2 disabled:opacity-50"
          >
            {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
          </button>
        </div>
      ) : (
        <div>
          {/*
            A link, not a fetch: the authorisation server has to see a top-level
            navigation, and an XHR redirect to another origin would be blocked.
          */}
          <a
            href="/api/lane/oauth/start"
            className="inline-flex rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
          >
            Connect Lane
          </a>
          <p className="mt-1.5 text-xs text-muted">
            Until you do, raising a task from a ticket will ask you to connect.
          </p>
        </div>
      )}

      {outcome === "denied" ? (
        <p className="mt-2 text-xs text-amber-600">
          Lane sign-in was cancelled.
        </p>
      ) : null}
      {outcome === "failed" || outcome === "invalid" ? (
        <p className="mt-2 text-xs text-red-600">
          Could not complete Lane sign-in. Try again.
        </p>
      ) : null}
    </Panel>
  );
}

/**
 * API keys for the public API.
 *
 * Personal rather than admin, for the same reason the Lane connection is: a key
 * acts as the agent who minted it and can do no more than they can, so it
 * belongs to the individual. An admin who wants an integration with admin rights
 * mints it from their own account, which also means the audit trail names a
 * person rather than "the integration".
 */
function ApiKeysPanel() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * The token, held only in this component's state and only until the panel is
   * dismissed. It is never written to the query cache: the cache is what the
   * list re-renders from, and a secret that outlives the moment it was shown is
   * a secret waiting to be screenshotted.
   */
  const [minted, setMinted] = useState<CreatedApiKey | null>(null);

  const keys = useQuery({ queryKey: queryKeys.apiKeys, queryFn: api.apiKeys });

  const create = useMutation({
    mutationFn: api.createApiKey,
    onSuccess: (key) => {
      setMinted(key);
      setCreating(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
    },
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not create the key"),
  });

  const revoke = useMutation({
    mutationFn: (id: number) => api.revokeApiKey(id),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys }),
  });

  return (
    <Panel
      title="API keys"
    >
      {minted ? <MintedKey token={minted.token} onDismiss={() => setMinted(null)} /> : null}

      {keys.data?.length ? (
        <ul className="mb-3 divide-y divide-line rounded-md border border-line">
          {keys.data.map((key) => (
            <li key={key.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm text-content">{key.name}</span>
                  <code className="rounded bg-surface-2 px-1 py-0.5 text-[11px] text-muted-on-tint">
                    {key.maskedToken}
                  </code>
                  <span className="text-[11px] text-faint">
                    {key.scope === "read_write" ? "Read & write" : "Read only"}
                  </span>
                  {key.attributeToKey ? (
                    <span
                      className="text-[11px] text-faint"
                      title={`Replies and notes from this key are signed "${key.name}" rather than with your name.`}
                    >
                      Posts as “{key.name}”
                    </span>
                  ) : null}
                </div>
                <p className="mt-0.5 text-[11px] text-faint">
                  {key.lastUsedAt
                    ? `Last used ${absoluteTime(key.lastUsedAt)}`
                    : "Never used"}
                  {key.expiresAt
                    ? ` · Expires ${formatInZone(key.expiresAt, { dateStyle: "medium" })}`
                    : ""}
                </p>
              </div>

              <button
                type="button"
                onClick={() => revoke.mutate(key.id)}
                disabled={revoke.isPending}
                className="shrink-0 rounded-md border border-line px-2 py-1 text-xs text-content hover:bg-surface-2 disabled:opacity-50"
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-3 text-xs text-muted">
          {keys.isPending ? "Loading…" : "You have no API keys."}
        </p>
      )}

      {creating ? (
        <NewKeyForm
          pending={create.isPending}
          onCancel={() => {
            setCreating(false);
            setError(null);
          }}
          onSubmit={(input) => create.mutate(input)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
        >
          Create a key
        </button>
      )}

      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}

      <p className="mt-3 text-xs text-muted">
        <a
          href="/api/v1/openapi.json"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-brand-600 dark:hover:text-brand-400"
        >
          OpenAPI description
        </a>{" "}
        of everything a key can call.
      </p>
    </Panel>
  );
}

/**
 * The one and only sighting of a new token.
 *
 * Deliberately loud, and deliberately not dismissible by clicking elsewhere:
 * only the hash is stored, so a key closed without being copied is gone and has
 * to be recreated.
 */
function MintedKey({ token, onDismiss }: { token: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="mb-3 rounded-md border border-brand-500 bg-brand-50 p-3 dark:bg-brand-950/40">
      <p className="text-xs font-medium text-content">
        Copy this now — it will not be shown again.
      </p>

      <div className="mt-2 flex items-center gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded bg-surface px-2 py-1 text-xs text-content">
          {token}
        </code>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(token).then(() => setCopied(true));
          }}
          className="shrink-0 rounded-md border border-line bg-surface px-2 py-1 text-xs text-content hover:bg-surface-2"
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-md px-2 py-1 text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          Done
        </button>
      </div>
    </div>
  );
}

function NewKeyForm({
  pending,
  onCancel,
  onSubmit,
}: {
  pending: boolean;
  onCancel: () => void;
  onSubmit: (input: {
    name: string;
    scope: ApiKeyScope;
    attributeToKey: boolean;
    expiresInDays: number | null;
  }) => void;
}) {
  const [name, setName] = useState("");
  const [scope, setScope] = useState<ApiKeyScope>("read_only");
  const [attributeToKey, setAttributeToKey] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState<number | null>(null);

  return (
    <form
      className="space-y-3 rounded-md border border-line p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim()) onSubmit({ name: name.trim(), scope, attributeToKey, expiresInDays });
      }}
    >
      <label className="block">
        <span className="block text-xs text-muted">What is it for?</span>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Zapier, nightly export, …"
          maxLength={120}
          autoFocus
          className="mt-1 w-full rounded-md border border-line bg-surface px-2 py-1 text-sm text-content"
        />
      </label>

      <fieldset>
        <legend className="text-xs text-muted">What may it do?</legend>
        <div className="mt-1 space-y-1">
          <Radio
            checked={scope === "read_only"}
            onChange={() => setScope("read_only")}
            label="Read only"
            hint="Can list and read tickets, and change nothing."
          />
          <Radio
            checked={scope === "read_write"}
            onChange={() => setScope("read_write")}
            label="Read and write"
            hint="Can also reply, raise tickets, and change their status — as you."
          />
        </div>
      </fieldset>

      <Toggle
        label={`Post as “${name.trim() || "the key"}” rather than as me`}
        hint="Replies it sends are signed with the key's name, so customers are not told a colleague wrote them. Leave off for a script that works on your behalf."
        checked={attributeToKey}
        onChange={setAttributeToKey}
      />

      <label className="block">
        <span className="block text-xs text-muted">Expires</span>
        <select
          value={expiresInDays === null ? "never" : String(expiresInDays)}
          onChange={(event) =>
            setExpiresInDays(
              event.target.value === "never" ? null : Number(event.target.value),
            )
          }
          className="mt-1 rounded-md border border-line bg-surface px-2 py-1 text-sm text-content"
        >
          <option value="never">Never</option>
          <option value="30">In 30 days</option>
          <option value="90">In 90 days</option>
          <option value="365">In a year</option>
        </select>
      </label>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={pending || !name.trim()}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {pending ? "Creating…" : "Create key"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-2 py-1 text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function Radio({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  hint: string;
}) {
  return (
    <label className="flex items-start gap-2">
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 accent-brand-600"
      />
      <span className="min-w-0">
        <span className="block text-sm text-content">{label}</span>
        <span className="block text-xs text-faint">{hint}</span>
      </span>
    </label>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

